// Carrier unlocks ordered from inside STORE OS on the owner's UnlockBase account (UnlockBase's XML
// API v2): the services, what each order status means, and how an order looks on the screen. The
// owner alone sees any of it. Shared by the server and the Unlocks screen.

/** How badly a tool needs a detail: never, if you have it, or it can't be ordered without. */
export type Need = 'none' | 'optional' | 'required';

/** One service UnlockBase sells (a "tool" in its API): an unlock for a carrier, brand or model. */
export interface UnlockbaseTool {
  id: string;
  name: string;
  /** The group UnlockBase lists it under: "AT&T USA", "iPhone", "Samsung Worldwide"… */
  group: string;
  /** What it costs the store, in UnlockBase credits (1 credit = 1 of the account's currency, normally USD). */
  credits: string;
  /** "1–3 days", "Instant", "Up to 24 hours", or null when UnlockBase doesn't say. */
  delivery: string | null;
  /** A note from UnlockBase about the service (what it won't do, what's needed). */
  message: string | null;
  /** The details the order must or may carry. */
  requires: {
    network: Need;
    mobile: Need;
    provider: Need;
    pin: Need;
    type: Need;
    kbh: Need;
    mep: Need;
    prd: Need;
    /** How many lock codes to ask for (Sony Ericsson); 0 means none. */
    locks: number;
  };
}

export interface UnlockbaseOption {
  id: string;
  name: string;
}

/**
 * sending   STORE OS is sending it to UnlockBase right now
 * waiting   UnlockBase has the order and is working on it
 * done      the code (or the result) is here
 * rejected  UnlockBase couldn't do it; the credits came back
 * canceled  the order was canceled before it was done
 * failed    UnlockBase said no before taking the order: nothing was charged
 * check     STORE OS couldn't tell whether the order went through: look at UnlockBase before ordering again
 */
export type UnlockbaseStatus = 'sending' | 'waiting' | 'done' | 'rejected' | 'canceled' | 'failed' | 'check';

export const UNLOCKBASE_STATUS: Record<UnlockbaseStatus, { label: string; tone: 'blue' | 'green' | 'amber' | 'red' | 'plain' }> = {
  sending: { label: 'Sending to UnlockBase', tone: 'blue' },
  waiting: { label: 'Waiting at UnlockBase', tone: 'blue' },
  done: { label: 'Code ready', tone: 'green' },
  rejected: { label: 'Not available (credits back)', tone: 'red' },
  canceled: { label: 'Canceled', tone: 'plain' },
  failed: { label: 'Not ordered', tone: 'red' },
  check: { label: 'Check UnlockBase', tone: 'amber' },
};

/** Orders STORE OS still asks UnlockBase about (one being sent has no order number yet). */
export const OPEN_UNLOCKBASE_STATUSES: UnlockbaseStatus[] = ['waiting', 'check'];

/**
 * What UnlockBase says about an order → our status. UnlockBase's Status is Waiting, Delivered or
 * Canceled; a delivered order is "Available" when the code came, and not when the provider
 * couldn't do it (the credits come back).
 */
export function unlockbaseStatusFrom(status: string | null | undefined, available: string | null | undefined, codes: string | null | undefined): UnlockbaseStatus {
  const s = String(status ?? '').trim().toLowerCase();
  if (s === 'canceled' || s === 'cancelled') return 'canceled';
  if (s === 'delivered') {
    if (String(available ?? '').trim().toLowerCase() === 'false' && !String(codes ?? '').trim()) return 'rejected';
    return 'done';
  }
  return 'waiting';
}

/** "1–3 days", "Instant", "Up to 24 hours", from UnlockBase's Delivery.Min / Max / Unit. */
export function deliveryLabel(min: string | null | undefined, max: string | null | undefined, unit: string | null | undefined): string | null {
  const u = String(unit ?? '').trim().toLowerCase();
  if (u === 'instant') return 'Instant';
  const lo = Number(String(min ?? '').trim());
  const hi = Number(String(max ?? '').trim());
  const okLo = Number.isFinite(lo) && lo > 0 && String(min ?? '').trim() !== '';
  const okHi = Number.isFinite(hi) && hi > 0 && String(max ?? '').trim() !== '';
  if (!okLo && !okHi) return null;
  const word = (n: number) => (u === 'day' ? (n === 1 ? 'day' : 'days') : u === 'hour' ? (n === 1 ? 'hour' : 'hours') : u || 'days');
  if (okLo && okHi) return lo === hi ? `${lo} ${word(lo)}` : `${lo}–${hi} ${word(hi)}`;
  if (okHi) return `Up to ${hi} ${word(hi)}`;
  return `From ${lo} ${word(lo)}`;
}

/** "6.00 credits" → "$6.00" when the account's currency is USD, else "6.00 EUR". */
/** UnlockBase's price in cents (its credits are dollars when the account is in USD); null when it isn't a USD amount. */
export function creditsToCents(credits: string | null | undefined, currency: string | null | undefined): number | null {
  const c = String(credits ?? '').trim();
  if (!c) return null;
  const n = Number(c);
  const cur = String(currency ?? 'USD').trim().toUpperCase();
  if (!Number.isFinite(n) || (cur && cur !== 'USD')) return null;
  return Math.round(n * 100);
}

export function creditsLabel(credits: string | null | undefined, currency: string | null | undefined): string {
  const c = String(credits ?? '').trim();
  if (!c) return '';
  const n = Number(c);
  const shown = Number.isFinite(n) ? n.toFixed(2) : c;
  const cur = String(currency ?? 'USD').trim().toUpperCase();
  return cur === 'USD' || !cur ? `$${shown}` : `${shown} ${cur}`;
}

/** The order as the screen gets it (never with the API key, never for anyone but the owner). */
export interface UnlockbaseOrder {
  id: string;
  /** UnlockBase's own order number, once it took the order. */
  ub_id: string | null;
  imei: string;
  tool_id: string;
  tool_name: string;
  group_name: string | null;
  credits: string | null;
  status: UnlockbaseStatus;
  status_label: string;
  /** The unlock code(s) or result, as UnlockBase sent them. */
  code: string | null;
  comments: string | null;
  network_name: string | null;
  mobile_name: string | null;
  customer_email: string | null;
  error: string | null;
  employee_name: string | null;
  created_at: number;
  updated_at: number;
  checked_at: number | null;
  delivered_at: number | null;
  /** What the customer pays for the unlock (typed when ordering, or set later); null until it's set. */
  charge_cents: number | null;
  /** What UnlockBase charged, in cents (nothing once an order is rejected, canceled or refused); null when the price isn't in dollars. */
  cost_cents: number | null;
  /** The charge minus the cost, once both are known. */
  profit_cents: number | null;
}

export interface UnlockbaseAccount {
  /** An API key is saved. */
  connected: boolean;
  email: string | null;
  credits: string | null;
  currency: string | null;
  /** When the balance was last asked for. */
  checked_at: number | null;
  /** Why the balance couldn't be read (a bad key, UnlockBase down). */
  error: string | null;
  /** Orders still waiting on UnlockBase. */
  open: number;
}

export interface UnlockbaseToolList {
  tools: UnlockbaseTool[];
  groups: string[];
  fetched_at: number;
  /** The list is from an earlier day and UnlockBase couldn't be asked again. */
  stale: boolean;
  error: string | null;
}

export interface UnlockbaseOrderInput {
  imei: string;
  tool: string;
  email?: string | null;
  comments?: string | null;
  network?: string | null;
  mobile?: string | null;
  provider?: string | null;
  pin?: string | null;
  type?: string | null;
  kbh?: string | null;
  mep?: string | null;
  prd?: string | null;
  locks?: string | null;
  /** What the customer pays for the unlock, in cents (optional; it can be set on the order later). */
  chargeCents?: number | null;
  /** Order again even though this IMEI has an open or recent order for the same service. */
  again?: boolean;
  /** The tap that sent it (the same tap sent twice orders once). */
  requestId?: string | null;
}
